import { Vector3 } from 'three';
import { Service } from '../Service';
/**
 * API for Viewer utility functions.
 *
 * TODO: Use EventRegister to handle cursor events. Rethink how to pass Model[] list to listeners.
 */
export class UtilityService extends Service {
    constructor(modules, frameResolvers) {
        super(modules);
        Object.defineProperty(this, "frameResolvers", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "cursorHoverListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "cursorTapListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.frameResolvers = frameResolvers;
        this.cursorHoverListener = () => { };
        this.cursorTapListener = () => { };
    }
    /**
     * Set the background color of the renderer.
     *
     * @param color
     */
    setBackgroundColor(color) {
        this.renderer.getWorld().setBackgroundColor(color);
    }
    /**
     * Get the maximum number of simultaneous in-flight network requests.
     *
     * @returns
     */
    getMaxNetworkRequestsInFlight() {
        return this.renderer.config.tilesetNetworkRequests.maxInFlight;
    }
    /**
     * Check if the renderer is sorting tileset network requests.
     *
     * @returns
     */
    isNetworkRequestSortingEnabled() {
        return this.renderer.config.tilesetNetworkRequests.requestSorting;
    }
    /**
     * Check if the renderer local file cache is enabled.
     *
     * @returns
     */
    isCacheEnabled() {
        return this.renderer.config.useLocalCache;
    }
    /**
     * Set the maximum number of simultaneous in-flight network requests.
     *
     * @param max
     */
    setMaxNetworkRequestsInFlight(max) {
        this.renderer.config.tilesetNetworkRequests.maxInFlight = max;
    }
    /**
     * Toggle the sorting of tileset network reqeusts.
     *
     * @param enabled
     */
    setNetworkRequestSortingEnabled(enabled) {
        this.renderer.config.tilesetNetworkRequests.requestSorting = enabled;
    }
    /**
     * Toggle the renderer's network cache.
     *
     * @param enabled
     */
    setCacheEnabled(enabled) {
        this.renderer.config.useLocalCache = enabled;
    }
    /**
     * Clear the renderer's network cache.
     */
    clearCache() {
        this.renderer.clearNetworkCache();
    }
    /**
     * Await the next frame.
     */
    nextFrame() {
        return new Promise(resolve => {
            this.frameResolvers.push(resolve);
        });
    }
    /**
     * Take a screenshot of the viewer.
     *
     * @returns
     */
    captureScene() {
        const template = document.createElement('canvas');
        const surface = template.getContext('2d');
        if (this.renderer === null || this.draw2D === null || surface === null) {
            return 'data:,';
        }
        const { width, height } = this.renderer.getCanvas();
        template.width = width;
        template.height = height;
        // Write the 3D renderer output onto the target
        surface.beginPath();
        surface.drawImage(this.renderer.getCanvas(), 0, 0, width, height);
        // Write the Draw2D output onto the target
        surface.beginPath();
        surface.drawImage(this.draw2D.getCanvas(), 0, 0, width, height);
        const quality = 1;
        return template.toDataURL('image/png', quality);
    }
    /**
     * Set the listener for cursor hover in 3D space.
     *
     * @param listener
     * @param models
     */
    setCursorHoverCallback(listener, models) {
        this.gestures.unregister('hover', this.cursorHoverListener);
        this.cursorHoverListener = (gesture) => {
            // TODO: Optimize raycasting so we could use it on hover
            const pick = this.renderer.pickScreenWorldSpaceSnapping(gesture.position);
            const position = new Vector3(pick.position.x, pick.position.y, pick.position.z);
            const userData = pick.userData;
            for (const model of models) {
                if (model.isPicked(pick)) {
                    listener(position, model, userData);
                    return;
                }
            }
            listener(position);
        };
        this.gestures.register('hover', this.cursorHoverListener);
    }
    /**
     * Set the listener for cursor taps in 3D space.
     *
     * @param listener
     * @param models
     */
    setCursorTapCallback(listener, models) {
        this.gestures.unregister('tap', this.cursorTapListener);
        this.cursorTapListener = (gesture) => {
            let pick = this.renderer.raycastScreen(gesture.position)[0];
            // Fallback to snapping GPU pick if raycast fails
            if (!pick)
                pick = this.renderer.pickScreenWorldSpaceSnapping(gesture.position);
            const position = new Vector3(pick.position.x, pick.position.y, pick.position.z);
            const userData = pick.userData;
            for (const model of models) {
                if (model.isPicked(pick)) {
                    listener(position, model, userData);
                    return;
                }
            }
            listener(position);
        };
        this.gestures.register('tap', this.cursorTapListener);
    }
    updateFrame(deltaTime) { }
}
